<template>
    <Head>
        <title>{{ pageData.props.page?.title }}</title>
    </Head>
    <div class="wrap teacher_wrap cms">
        <header class="lesson">
            <div class="header_left">
                <div class="activity_img white">
                    <svg class="ico_hat" fill="none">
                        <use xlink:href="/assets/img/ico/icon-sprite.svg#ico_hat"></use>
                    </svg>
                </div>
                <div class="lesson_info">
                    <div class="lesson_info_title">
                        <h1>{{ $t('pages.preSettingModules.title') }} </h1>
                    </div>
                </div>
            </div>
        </header>
        <div class="admin_wrapper">
            <ModuleGuideSidebar
                :modules="modules"
                :active-module-type="activeModuleType"
                @select-module="setActiveModuleType"
            />
            <div class="board_wrapper">
                <div ref="contentRef" class="module_guide_wrapper">
                    <ModuleGuide v-for="module in modules" :key="`${module.type}-${module.id}`" :module="module" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
    import { ModuleTypeEnum } from '@/enums/ModuleTypeEnum';
    import { usePage, Head } from '@inertiajs/vue3';
    import { PageProps } from '@/teacher/types';
    import { ModuleGuide as ModuleGuideModel } from '@/app/api/models/moduleGuide';
    import ModuleGuide from '../components/modules/guide/ModuleGuide.vue';
    import ModuleGuideSidebar from '../components/modules/guide/ModuleGuideSidebar.vue';

    const pageData = usePage<PageProps>();
    const moduleGuides = computed(() => pageData.props.module_guides as ModuleGuideModel[]);

    const modules = computed(() => {
        return moduleGuides.value.map((guide) => ({
            id: 0,
            type: guide.type,
            mode: guide.mode ?? [],
            code: 'pre-setting-modules',
            title: '수업 설정',
            contents: [],
            questions: [],
            txt_type: '',
            created_at: '',
            updated_at: '',
            deleted_at: '',
            guide: guide,
            pages: [],
        }));
    });

    // 현재 활성화된(화면에 보이는) 모듈 타입
    const activeModuleType = ref(modules.value[0]?.type);

    // 모듈 가이드 컨테이너의 DOM 참조
    const contentRef = ref<HTMLElement>();

    // 브라우저 관찰자 객체 (Intersection Observer)
    let observer: IntersectionObserver | null = null;

    // 사이드바 클릭을 통한 자동 스크롤 중인지 여부 (이동 중 옵저버 업데이트 방지용)
    const isManualScrolling = ref(false);

    // 스크롤 완료 후 플래그 해제를 위한 타이머
    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

    /**
     * 사이드바 클릭 시 해당 모듈로 부드럽게 스크롤 이동
     *
     * @param moduleType 이동할 모듈의 타입
     */
    function setActiveModuleType(moduleType: ModuleTypeEnum) {
        const target = document.getElementById(`module_${moduleType}`);
        if (!target || !contentRef.value) return;

        // 1. 수동 이동 시작: 옵저버가 중간 모듈들을 활성화하지 않도록 잠금
        isManualScrolling.value = true;
        activeModuleType.value = moduleType;

        // 2. 해당 요소의 위치(상단 여백 24px 고려)로 부드럽게 이동
        contentRef.value.scrollTo({
            top: target.offsetTop - 24,
            behavior: 'smooth',
        });

        // 3. 이동이 완료될 즈음(1초 후) 잠금 해제하여 다시 스크롤 감지 시작
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
            isManualScrolling.value = false;
        }, 1000);
    }

    /**
     * IntersectionObserver 설정
     * 사용자가 직접 스크롤할 때, 화면 상단 기준선(24px)에 걸리는 모듈을 감지하여 사이드바 상태를 동기화합니다.
     */
    onMounted(async () => {
        // DOM 렌더링 완료 대기
        await nextTick();
        if (!contentRef.value) return;

        // 교차 감지 옵저버 생성
        observer = new IntersectionObserver(
            (entries) => {
                // 사이드바 클릭으로 자동 이동 중일 때는 옵저버의 상태 업데이트를 무시 (깜빡임 방지)
                if (isManualScrolling.value) return;

                entries.forEach((entry) => {
                    // 요소가 지정된 감시 영역(rootMargin)에 들어오면 상태 업데이트
                    if (entry.isIntersecting) {
                        const type = Number(entry.target.id.split('_')[1]) as ModuleTypeEnum;
                        activeModuleType.value = type;
                    }
                });
            },
            {
                // 감시 기준 컨테이너
                root: contentRef.value,
                // 감시 영역 설정: 상단 24px 지점을 얇은 선처럼 기준으로 삼음
                // 상단 24px 아래부터 감시 시작(-24px), 하단은 거의 무시(-95%)
                rootMargin: '-24px 0px -95% 0px',
                // 요소가 조금이라도 기준선에 닿으면 감지
                threshold: 0,
            },
        );

        // id가 'module_'로 시작하는 모든 자식 요소들을 감시 대상으로 등록
        contentRef.value.querySelectorAll('[id^="module_"]').forEach((el) => {
            observer?.observe(el);
        });
    });

    /**
     * 컴포넌트 해제 시 옵저버 및 타이머 정리
     */
    onBeforeUnmount(() => {
        if (observer) observer.disconnect();
        if (scrollTimeout) clearTimeout(scrollTimeout);
    });
</script>

<style scoped></style>
